import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, Avatar, Chip,
  Button, IconButton, TextField, InputAdornment, Tabs, Tab, Paper, Stack,
  CircularProgress, Tooltip, useMediaQuery, useTheme, Dialog, DialogTitle,
  DialogContent, DialogActions, MenuItem, Select, InputLabel, FormControl,
  ToggleButtonGroup, ToggleButton, Switch, FormControlLabel,
} from '@mui/material';
import {
  Add, Diversity3 as ClubIcon, PersonAdd, ExitToApp, Delete, ContentCopy, Check, Close,
  Search, PersonSearch, GroupsRounded, HowToReg, PhotoCamera, Settings,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import AppNavigation from './AppNavigation';
import SignInNudge from './SignInNudge';
import PostCard from './PostCard';
import MentionTextField from './MentionTextField';
import { useClubs } from '../hooks/useClubs';
import { authService } from '../services/authService';
import { useNotification } from './NotificationContext';
import {
  clubService, DiveClub, ClubMembership, ClubJoinMode, ClubJoinModeValue, ClubRole, ClubMemberType, clubPhotoUrl,
} from '../services/clubService';
import { groupService, DiveGroup } from '../services/groupService';
import { userProfileService } from '../services/userProfileService';
import MemberPhotoGrid from './MemberPhotoGrid';
import { postService, Post } from '../services/postService';
import { buddyService, UserPublicProfile } from '../services/buddyService';
import { scheduledDivesService } from '../services/scheduledDivesService';
import type { ScheduledDive } from '../types/ScheduledDive';
import ScheduleDiveDialog from './ScheduleDiveDialog';
import { eventService, DiveEvent } from '../services/eventService';
import EventsPanel from './EventsPanel';
import { format } from 'date-fns';

function initials(name: string) {
  return name.split(' ').filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('');
}

function ClubAvatar({ name, size = 48, photoUrl }: { name: string; size?: number; photoUrl?: string | null }) {
  return (
    <Avatar src={photoUrl ?? undefined} sx={{ width: size, height: size, bgcolor: 'primary.main', fontWeight: 700, fontSize: size * 0.35 }}>
      {!photoUrl && (initials(name) || '?')}
    </Avatar>
  );
}

// ── Create club dialog ───────────────────────────────────────────────────────

function CreateClubDialog({
  open, onClose, onCreate,
}: {
  open: boolean;
  onClose: () => void;
  onCreate: (name: string, description: string, joinMode: ClubJoinModeValue) => Promise<void>;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [joinMode, setJoinMode] = useState<ClubJoinModeValue>(ClubJoinMode.InviteOnly);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!name.trim()) return;
    setSubmitting(true);
    try {
      await onCreate(name.trim(), description.trim(), joinMode);
      setName('');
      setDescription('');
      setJoinMode(ClubJoinMode.InviteOnly);
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>New Dive Club</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Club name"
            value={name}
            onChange={e => setName(e.target.value)}
            fullWidth
            autoFocus
          />
          <TextField
            label="Description (optional)"
            value={description}
            onChange={e => setDescription(e.target.value)}
            fullWidth
            multiline
            minRows={2}
          />
          <FormControl fullWidth>
            <InputLabel id="club-join-mode-label">Who can join</InputLabel>
            <Select
              labelId="club-join-mode-label"
              label="Who can join"
              value={joinMode}
              onChange={e => setJoinMode(e.target.value as ClubJoinModeValue)}
            >
              <MenuItem value={ClubJoinMode.InviteOnly}>Invite only — admins add members, or approve requests</MenuItem>
              <MenuItem value={ClubJoinMode.OpenLink}>Open link — anyone with the code can join instantly</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!name.trim() || submitting}
          startIcon={submitting ? <CircularProgress size={16} /> : undefined}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Club settings dialog ─────────────────────────────────────────────────────

function ClubSettingsDialog({
  open, onClose, club, onSave,
}: {
  open: boolean;
  onClose: () => void;
  club: DiveClub;
  onSave: (searchable: boolean, showOnPublicPage: boolean) => Promise<void>;
}) {
  const [searchable, setSearchable] = useState(club.searchable);
  const [showOnPublicPage, setShowOnPublicPage] = useState(club.showOnPublicPage ?? false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { setSearchable(club.searchable); setShowOnPublicPage(club.showOnPublicPage ?? false); }, [club.searchable, club.showOnPublicPage, open]);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await onSave(searchable, showOnPublicPage);
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Club Settings</DialogTitle>
      <DialogContent>
        <FormControlLabel
          sx={{ mt: 1 }}
          control={<Switch checked={searchable} onChange={e => setSearchable(e.target.checked)} />}
          label={
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>Show this club in search</Typography>
              <Typography variant="caption" color="text.secondary">
                When off, this club won't appear in club search results. Members can still find it via an invite or the join link — this only controls discoverability.
              </Typography>
            </Box>
          }
        />
        <FormControlLabel
          sx={{ mt: 2 }}
          control={<Switch checked={showOnPublicPage} onChange={e => setShowOnPublicPage(e.target.checked)} />}
          label={
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>Show on the public home page</Typography>
              <Typography variant="caption" color="text.secondary">
                Lets people who aren't signed in see this club's name, description and member count on the home page. Off by default — nothing else about your club or its members is shown.
              </Typography>
            </Box>
          }
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={16} /> : undefined}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Join by code dialog ──────────────────────────────────────────────────────

function JoinByCodeDialog({
  open, onClose, onJoin,
}: {
  open: boolean;
  onClose: () => void;
  onJoin: (code: string) => Promise<void>;
}) {
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!code.trim()) return;
    setSubmitting(true);
    try {
      await onJoin(code.trim());
      setCode('');
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Join a Club</DialogTitle>
      <DialogContent>
        <TextField
          label="Club code"
          value={code}
          onChange={e => setCode(e.target.value)}
          fullWidth
          autoFocus
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!code.trim() || submitting}
          startIcon={submitting ? <CircularProgress size={16} /> : undefined}
        >
          Join
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Clubs list (index) ───────────────────────────────────────────────────────

const DiveClubsList: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { showSuccess, showError } = useNotification();
  const navigate = useNavigate();

  const [currentUser, setCurrentUser] = useState(authService.getUser());
  const userId = currentUser?.userId ?? null;
  const { clubs, loading, error, createClub, joinByCode } = useClubs(userId);

  const [createOpen, setCreateOpen] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);

  const handleCreate = useCallback(async (name: string, description: string, joinMode: ClubJoinModeValue) => {
    try {
      const club = await createClub(name, description || undefined, joinMode);
      showSuccess('Club created!');
      navigate(`/clubs/${club.clubId}`);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to create club');
    }
  }, [createClub, showSuccess, showError, navigate]);

  const handleJoin = useCallback(async (code: string) => {
    try {
      const club = await joinByCode(code);
      showSuccess(`Joined "${club.name}"`);
      navigate(`/clubs/${club.clubId}`);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to join club');
    }
  }, [joinByCode, showSuccess, showError, navigate]);

  if (!userId) {
    return (
      <Box>
        <AppNavigation />
        <SignInNudge
          feature="join dive clubs"
          description="Join a club made up of divers and whole dive groups to plan, share, and post together — sign in or create a free account to get started."
          onSignedIn={() => setCurrentUser(authService.getUser())}
        />
      </Box>
    );
  }

  return (
    <Box>
      <AppNavigation />
      <Box sx={{ px: { xs: 2, sm: 3 }, pb: 4, maxWidth: 900, mx: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: { xs: 2, sm: 3 }, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Dive Clubs</Typography>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" onClick={() => setJoinOpen(true)} sx={{ minHeight: 40 }}>
              Join by Code
            </Button>
            <Button variant="contained" startIcon={<Add />} onClick={() => setCreateOpen(true)} sx={{ minHeight: 40 }}>
              New Club
            </Button>
          </Stack>
        </Box>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}

        {!loading && clubs.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <ClubIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              No dive clubs yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create a club, or join one with a code.
            </Typography>
          </Box>
        )}

        <Grid container spacing={2}>
          {clubs.map(club => (
            <Grid item xs={12} sm={6} md={isMobile ? 12 : 4} key={club.clubId}>
              <Card sx={{ height: '100%', cursor: 'pointer' }} onClick={() => navigate(`/clubs/${club.clubId}`)}>
                <CardContent>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <ClubAvatar name={club.name} photoUrl={club.photoKey ? clubPhotoUrl(club.photoKey) : null} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{club.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {club.memberCount} member{club.memberCount === 1 ? '' : 's'}
                      </Typography>
                    </Box>
                  </Stack>
                  {club.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                      {club.description}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <CreateClubDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreate={handleCreate} />
        <JoinByCodeDialog open={joinOpen} onClose={() => setJoinOpen(false)} onJoin={handleJoin} />
      </Box>
    </Box>
  );
};

// ── Add member panel: search users OR pick one of the caller's own groups ──

function AddMemberPanel({
  existingMemberIds,
  onAdd,
  loadingId,
}: {
  existingMemberIds: Set<string>;
  onAdd: (memberId: string, memberType: 'User' | 'Group') => void;
  loadingId: string | null;
}) {
  const [mode, setMode] = useState<'User' | 'Group'>('User');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<UserPublicProfile[]>([]);
  const [searching, setSearching] = useState(false);
  const [myGroups, setMyGroups] = useState<DiveGroup[]>([]);
  const [groupsLoaded, setGroupsLoaded] = useState(false);

  useEffect(() => {
    if (mode !== 'Group' || groupsLoaded) return;
    groupService.getMyGroups().then(groups => { setMyGroups(groups); setGroupsLoaded(true); }).catch(() => setGroupsLoaded(true));
  }, [mode, groupsLoaded]);

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    if (value.length < 2) { setResults([]); return; }
    setSearching(true);
    try {
      const found = await buddyService.searchUsers(value);
      setResults(found);
    } finally {
      setSearching(false);
    }
  };

  const filteredResults = results.filter(u => !existingMemberIds.has(u.userId));
  const filteredGroups = myGroups.filter(g => !existingMemberIds.has(g.groupId));

  return (
    <Stack spacing={1.5}>
      <ToggleButtonGroup value={mode} exclusive onChange={(_, v) => v && setMode(v)} size="small" fullWidth>
        <ToggleButton value="User">Add a person</ToggleButton>
        <ToggleButton value="Group">Add a whole group</ToggleButton>
      </ToggleButtonGroup>

      {mode === 'User' ? (
        <>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by name or email…"
            value={query}
            onChange={handleChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {searching ? <CircularProgress size={16} /> : <Search fontSize="small" />}
                </InputAdornment>
              ),
            }}
            autoComplete="off"
          />

          {query.length >= 2 && filteredResults.length === 0 && !searching && (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <PersonSearch sx={{ fontSize: 36, color: 'text.disabled', mb: 0.5 }} />
              <Typography variant="body2" color="text.secondary">No users found for "{query}"</Typography>
            </Box>
          )}

          {filteredResults.length > 0 && (
            <Stack spacing={1}>
              {filteredResults.map(u => (
                <Paper key={u.userId} variant="outlined" sx={{ p: 1.5 }}>
                  <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
                    <ClubAvatar name={u.displayName} size={36} />
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{u.displayName}</Typography>
                      <Typography variant="caption" color="text.secondary">{u.diveCountLabel}</Typography>
                    </Box>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={loadingId === u.userId ? <CircularProgress size={14} /> : <PersonAdd />}
                      disabled={loadingId === u.userId}
                      onClick={() => onAdd(u.userId, 'User')}
                      sx={{ minHeight: 36 }}
                    >
                      Add
                    </Button>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          )}
        </>
      ) : (
        <Stack spacing={1}>
          {groupsLoaded && filteredGroups.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <GroupsRounded sx={{ fontSize: 36, color: 'text.disabled', mb: 0.5 }} />
              <Typography variant="body2" color="text.secondary">No groups available to add</Typography>
            </Box>
          )}
          {filteredGroups.map(g => (
            <Paper key={g.groupId} variant="outlined" sx={{ p: 1.5 }}>
              <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
                <Avatar sx={{ width: 36, height: 36, bgcolor: 'secondary.main' }}>
                  <GroupsRounded sx={{ fontSize: 18 }} />
                </Avatar>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{g.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {g.memberCount} member{g.memberCount === 1 ? '' : 's'} — all get club access
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={loadingId === g.groupId ? <CircularProgress size={14} /> : <GroupsRounded />}
                  disabled={loadingId === g.groupId}
                  onClick={() => onAdd(g.groupId, 'Group')}
                  sx={{ minHeight: 36 }}
                >
                  Add
                </Button>
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}
    </Stack>
  );
}

// ── Club detail (members + feed) ────────────────────────────────────────────

const DiveClubDetail: React.FC = () => {
  const { clubId } = useParams<{ clubId: string }>();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const [currentUser, setCurrentUser] = useState(authService.getUser());
  const userId = currentUser?.userId ?? null;

  const [club, setClub] = useState<DiveClub | null>(null);
  const [members, setMembers] = useState<ClubMembership[]>([]);
  const [requests, setRequests] = useState<ClubMembership[]>([]);
  const [posts, setPosts] = useState<Post[]>([]);
  const [openDives, setOpenDives] = useState<ScheduledDive[]>([]);
  const [events, setEvents] = useState<DiveEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState(0);
  const [postText, setPostText] = useState('');
  const [postTaggedUserIds, setPostTaggedUserIds] = useState<string[]>([]);
  const [postTaggedGroupIds, setPostTaggedGroupIds] = useState<string[]>([]);
  const [postTaggedClubIds, setPostTaggedClubIds] = useState<string[]>([]);
  const [posting, setPosting] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);
  const [requestingJoin, setRequestingJoin] = useState(false);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [joiningDiveId, setJoiningDiveId] = useState<string | null>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const photoInputRef = useRef<HTMLInputElement>(null);

  const myMembership = members.find(m => m.memberId === userId && m.memberType === ClubMemberType.User);
  const canManage = myMembership?.role === ClubRole.Admin || myMembership?.role === ClubRole.Owner;

  const load = useCallback(async () => {
    if (!clubId) return;
    setLoading(true);
    setError(null);
    setAccessDenied(false);
    try {
      const [c, m] = await Promise.all([
        clubService.getClub(clubId),
        clubService.getMembers(clubId),
      ]);
      setClub(c);
      setMembers(m);
      try {
        const f = await postService.getClubFeed(clubId);
        setPosts(f);
      } catch {
        setPosts([]);
      }
      try {
        const d = await scheduledDivesService.getOpenDivesForClub(clubId);
        setOpenDives(d);
      } catch {
        setOpenDives([]);
      }
      try {
        const ev = await eventService.getEventsForClub(clubId);
        setEvents(ev);
      } catch {
        setEvents([]);
      }
    } catch {
      // Could be a 404 (no such club) or 403 (not a member) — either way offer request-to-join
      // rather than a bare error, since InviteOnly clubs let a non-member request access.
      setAccessDenied(true);
    } finally {
      setLoading(false);
    }
  }, [clubId]);

  useEffect(() => { load(); }, [load]);

  const handleJoinDive = useCallback(async (diveId: string) => {
    setJoiningDiveId(diveId);
    try {
      const updated = await scheduledDivesService.joinOpenDive(diveId);
      setOpenDives(prev => prev.map(d => (d.diveId === diveId ? updated : d)));
      showSuccess('Joined the dive');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to join dive');
    } finally {
      setJoiningDiveId(null);
    }
  }, [showSuccess, showError]);

  useEffect(() => {
    if (!clubId || !canManage) { setRequests([]); return; }
    clubService.getPendingRequests(clubId).then(setRequests).catch(() => setRequests([]));
  }, [clubId, canManage]);

  const handlePost = useCallback(async () => {
    if (!clubId || !postText.trim()) return;
    setPosting(true);
    try {
      const post = await postService.createClubPost(clubId, {
        text: postText.trim(),
        taggedUserIds: postTaggedUserIds,
        taggedGroupIds: postTaggedGroupIds,
        taggedClubIds: postTaggedClubIds,
      });
      setPosts(prev => [post, ...prev]);
      setPostText('');
      setPostTaggedUserIds([]);
      setPostTaggedGroupIds([]);
      setPostTaggedClubIds([]);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to post');
    } finally {
      setPosting(false);
    }
  }, [clubId, postText, postTaggedUserIds, postTaggedGroupIds, postTaggedClubIds, showError]);

  const handleDeletePost = useCallback(async (postId: string) => {
    try {
      await postService.deletePost(postId);
      setPosts(prev => prev.filter(p => p.postId !== postId));
      showSuccess('Post deleted');
    } catch {
      showError('Failed to delete post');
    }
  }, [showSuccess, showError]);

  // Pinning a post unpins whatever was previously pinned (server-side), so reflect that
  // locally too: clear isPinned on every other post, then re-sort pinned-first.
  const handlePinChanged = useCallback((updated: Post) => {
    setPosts(prev => {
      const next = prev.map(p => {
        if (p.postId === updated.postId) return updated;
        return updated.isPinned && p.isPinned ? { ...p, isPinned: false } : p;
      });
      return [...next].sort((a, b) => Number(b.isPinned) - Number(a.isPinned));
    });
  }, []);

  const handleAddMember = useCallback(async (memberId: string, memberType: 'User' | 'Group') => {
    if (!clubId) return;
    setActionLoadingId(memberId);
    try {
      const added = await clubService.inviteMember(clubId, memberId, memberType);
      showSuccess(`${added.displayName} added`);
      await load();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to add member');
    } finally {
      setActionLoadingId(null);
    }
  }, [clubId, showSuccess, showError, load]);

  const handleApprove = useCallback(async (targetId: string) => {
    if (!clubId) return;
    setActionLoadingId(targetId);
    try {
      await clubService.approveRequest(clubId, targetId);
      showSuccess('Request approved');
      setRequests(prev => prev.filter(r => r.memberId !== targetId));
      await load();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to approve request');
    } finally {
      setActionLoadingId(null);
    }
  }, [clubId, showSuccess, showError, load]);

  const handleDecline = useCallback(async (targetId: string) => {
    if (!clubId) return;
    setActionLoadingId(targetId);
    try {
      await clubService.declineOrCancelRequest(clubId, targetId);
      setRequests(prev => prev.filter(r => r.memberId !== targetId));
    } catch {
      showError('Failed to decline request');
    } finally {
      setActionLoadingId(null);
    }
  }, [clubId, showError]);

  const handleRemoveMember = useCallback(async (targetId: string) => {
    if (!clubId) return;
    setActionLoadingId(targetId);
    try {
      await clubService.removeMember(clubId, targetId);
      setMembers(prev => prev.filter(m => m.memberId !== targetId));
      showSuccess('Member removed');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to remove member');
    } finally {
      setActionLoadingId(null);
    }
  }, [clubId, showSuccess, showError]);

  const handleLeave = useCallback(async () => {
    if (!clubId || !userId) return;
    try {
      await clubService.removeMember(clubId, userId);
      showSuccess('Left club');
      navigate('/clubs');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to leave club');
    }
  }, [clubId, userId, showSuccess, showError, navigate]);

  const handleDeleteClub = useCallback(async () => {
    if (!clubId) return;
    try {
      await clubService.deleteClub(clubId);
      showSuccess('Club deleted');
      navigate('/clubs');
    } catch {
      showError('Failed to delete club');
    }
  }, [clubId, showSuccess, showError, navigate]);

  const handleCopyCode = useCallback(() => {
    if (!club) return;
    navigator.clipboard?.writeText(club.shareCode).then(() => showSuccess('Code copied'));
  }, [club, showSuccess]);

  const handleSaveSettings = useCallback(async (searchable: boolean, showOnPublicPage: boolean) => {
    if (!clubId) return;
    try {
      const updated = await clubService.updateClub(clubId, { searchable, showOnPublicPage });
      setClub(updated);
      showSuccess('Club settings updated');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to update club settings');
      throw err;
    }
  }, [clubId, showSuccess, showError]);

  // Reuses the same presigned-upload flow profile photos use — same public bucket, same
  // allowlist — there's no club-specific upload endpoint, nor does one need to exist.
  const handlePhotoSelected = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !clubId) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      showError('Please choose a JPEG, PNG, or WEBP image');
      return;
    }
    setUploadingPhoto(true);
    try {
      const photoKey = await userProfileService.uploadPhoto(file);
      const updated = await clubService.updateClub(clubId, { photoKey });
      setClub(updated);
      showSuccess('Club photo updated');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to update club photo');
    } finally {
      setUploadingPhoto(false);
      e.target.value = '';
    }
  }, [clubId, showSuccess, showError]);

  const handleRequestJoin = useCallback(async () => {
    if (!clubId) return;
    setRequestingJoin(true);
    try {
      await clubService.requestJoin(clubId);
      showSuccess('Request sent — an admin will need to approve it');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to request to join');
    } finally {
      setRequestingJoin(false);
    }
  }, [clubId, showSuccess, showError]);

  if (!userId) {
    return (
      <Box>
        <AppNavigation />
        <SignInNudge
          feature="view this dive club"
          description="Sign in or create a free account to view this club."
          onSignedIn={() => setCurrentUser(authService.getUser())}
        />
      </Box>
    );
  }

  if (loading) {
    return (
      <Box>
        <AppNavigation />
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      </Box>
    );
  }

  if (accessDenied || error || !club) {
    return (
      <Box>
        <AppNavigation />
        <Box sx={{ px: 3, py: 4, textAlign: 'center' }}>
          <ClubIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            {error ?? "You don't have access to this club yet"}
          </Typography>
          {accessDenied && (
            <Button
              variant="contained"
              startIcon={requestingJoin ? <CircularProgress size={16} /> : <HowToReg />}
              disabled={requestingJoin}
              onClick={handleRequestJoin}
            >
              Request to join
            </Button>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <AppNavigation />
      <Box sx={{ px: { xs: 2, sm: 3 }, pb: 4, maxWidth: 800, mx: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box sx={{ position: 'relative' }}>
              <ClubAvatar name={club.name} size={64} photoUrl={club.photoKey ? clubPhotoUrl(club.photoKey) : null} />
              {canManage && (
                <Tooltip title="Change club photo">
                  <IconButton
                    size="small"
                    onClick={() => photoInputRef.current?.click()}
                    disabled={uploadingPhoto}
                    sx={{
                      position: 'absolute', bottom: -4, right: -4, bgcolor: 'background.paper',
                      border: '1px solid', borderColor: 'divider', width: 24, height: 24,
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    {uploadingPhoto ? <CircularProgress size={12} /> : <PhotoCamera sx={{ fontSize: 14 }} />}
                  </IconButton>
                </Tooltip>
              )}
              <input ref={photoInputRef} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={handlePhotoSelected} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>{club.name}</Typography>
              <Typography variant="caption" color="text.secondary">
                {club.memberCount} member{club.memberCount === 1 ? '' : 's'} · {club.joinMode === ClubJoinMode.OpenLink ? 'Open link' : 'Invite only'}
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1}>
            {canManage && (
              <Tooltip title="Club settings">
                <IconButton onClick={() => setSettingsOpen(true)}><Settings /></IconButton>
              </Tooltip>
            )}
            {myMembership?.role === ClubRole.Owner ? (
              <Button color="error" startIcon={<Delete />} onClick={handleDeleteClub}>Delete Club</Button>
            ) : (
              <Button color="error" startIcon={<ExitToApp />} onClick={handleLeave}>Leave Club</Button>
            )}
          </Stack>
        </Box>

        {club.description && (
          <Typography variant="body1" sx={{ mb: 1.5 }}>{club.description}</Typography>
        )}

        <MemberPhotoGrid
          members={members.map(m => ({ id: m.memberId, displayName: m.displayName, profilePhotoUrl: m.profilePhotoUrl }))}
        />
        <Box sx={{ mb: 2 }} />

        {club.joinMode === ClubJoinMode.OpenLink && canManage && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Typography variant="body2">Join code: <strong>{club.shareCode}</strong></Typography>
            <Button size="small" startIcon={<ContentCopy />} onClick={handleCopyCode}>Copy</Button>
          </Paper>
        )}

        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Feed" sx={{ textTransform: 'none', fontWeight: 600, minHeight: 44 }} />
          <Tab label={`Open Dives (${openDives.length})`} sx={{ textTransform: 'none', fontWeight: 600, minHeight: 44 }} />
          <Tab label={`Events (${events.length})`} sx={{ textTransform: 'none', fontWeight: 600, minHeight: 44 }} />
          <Tab label={`Members (${members.length})`} sx={{ textTransform: 'none', fontWeight: 600, minHeight: 44 }} />
          {canManage && (
            <Tab label={`Requests${requests.length ? ` (${requests.length})` : ''}`} sx={{ textTransform: 'none', fontWeight: 600, minHeight: 44 }} />
          )}
        </Tabs>

        {tab === 0 && (
          <Box>
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Stack spacing={1.5}>
                <MentionTextField
                  placeholder={`Share something with ${club.name}… Type @ to tag a buddy, group, or club`}
                  value={postText}
                  onChange={setPostText}
                  taggedUserIds={postTaggedUserIds}
                  onTaggedUserIdsChange={setPostTaggedUserIds}
                  taggedGroupIds={postTaggedGroupIds}
                  onTaggedGroupIdsChange={setPostTaggedGroupIds}
                  taggedClubIds={postTaggedClubIds}
                  onTaggedClubIdsChange={setPostTaggedClubIds}
                  fullWidth
                  multiline
                  minRows={2}
                />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    onClick={handlePost}
                    disabled={!postText.trim() || posting}
                    startIcon={posting ? <CircularProgress size={16} /> : undefined}
                  >
                    Post
                  </Button>
                </Box>
              </Stack>
            </Paper>

            {posts.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body2" color="text.secondary">No posts yet — be the first to share something.</Typography>
              </Box>
            )}

            {posts.map(post => (
              <PostCard
                key={post.postId}
                post={post}
                currentUserId={userId}
                onDelete={handleDeletePost}
                canManageGroupOrClub={canManage}
                onPinChanged={handlePinChanged}
              />
            ))}
          </Box>
        )}

        {tab === 1 && (
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="contained" size="small" startIcon={<Add />} onClick={() => setScheduleDialogOpen(true)}>
                Schedule an Open Dive
              </Button>
            </Box>

            {openDives.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  No open dives yet — schedule one for the club to join.
                </Typography>
              </Box>
            )}

            {openDives.map(dive => {
              const alreadyJoined = dive.ownerUserId === userId
                || dive.invitees.some(i => i.userId === userId && i.status === 'accepted');
              return (
                <Paper key={dive.diveId} variant="outlined" sx={{ p: 2 }}>
                  <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {dive.siteName || 'Dive'} · {format(new Date(dive.scheduledDate), 'MMM d, yyyy')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Organized by {dive.ownerDisplayName}
                        {dive.charterCompany ? ` · ${dive.charterCompany}` : ''}
                      </Typography>
                    </Box>
                    {alreadyJoined ? (
                      <Chip label={dive.ownerUserId === userId ? 'You organized this' : "You're in"} size="small" color="primary" />
                    ) : (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleJoinDive(dive.diveId)}
                        disabled={joiningDiveId === dive.diveId}
                        startIcon={joiningDiveId === dive.diveId ? <CircularProgress size={14} /> : undefined}
                      >
                        Join
                      </Button>
                    )}
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
        )}

        {tab === 2 && clubId && (
          <EventsPanel clubId={clubId} currentUserId={userId} />
        )}

        {tab === 3 && (
          <Stack spacing={2}>
            {canManage && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>Add a member</Typography>
                <AddMemberPanel
                  existingMemberIds={new Set(members.map(m => m.memberId))}
                  onAdd={handleAddMember}
                  loadingId={actionLoadingId}
                />
              </Paper>
            )}

            {members.map(member => (
              <Paper key={member.memberId} variant="outlined" sx={{ p: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
                  {member.memberType === ClubMemberType.Group ? (
                    <Avatar sx={{ width: 40, height: 40, bgcolor: 'secondary.main' }}>
                      <GroupsRounded sx={{ fontSize: 20 }} />
                    </Avatar>
                  ) : (
                    <ClubAvatar name={member.displayName} size={40} photoUrl={member.profilePhotoUrl} />
                  )}
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{member.displayName}</Typography>
                    {member.memberType === ClubMemberType.Group && (
                      <Typography variant="caption" color="text.secondary">Group — all active members have access</Typography>
                    )}
                  </Box>
                  {member.memberType === ClubMemberType.User && (
                    <Chip
                      label={member.role}
                      size="small"
                      color={member.role === ClubRole.Owner ? 'primary' : member.role === ClubRole.Admin ? 'secondary' : 'default'}
                    />
                  )}
                  {canManage && !(member.memberType === ClubMemberType.User && member.role === ClubRole.Owner) && member.memberId !== userId && (
                    <Tooltip title="Remove member">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveMember(member.memberId)}
                        disabled={actionLoadingId === member.memberId}
                        sx={{ minHeight: 40, minWidth: 40 }}
                      >
                        {actionLoadingId === member.memberId ? <CircularProgress size={16} /> : <Delete fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              </Paper>
            ))}
          </Stack>
        )}

        {tab === 4 && canManage && (
          <Stack spacing={1.5}>
            {requests.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body2" color="text.secondary">No pending requests</Typography>
              </Box>
            )}
            {requests.map(req => (
              <Paper key={req.memberId} variant="outlined" sx={{ p: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
                  <ClubAvatar name={req.displayName} size={40} />
                  <Typography variant="body2" sx={{ fontWeight: 600, flexGrow: 1 }}>{req.displayName}</Typography>
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={actionLoadingId === req.memberId ? <CircularProgress size={14} /> : <Check />}
                      disabled={actionLoadingId === req.memberId}
                      onClick={() => handleApprove(req.memberId)}
                      sx={{ minHeight: 36 }}
                    >
                      Approve
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      startIcon={<Close />}
                      disabled={actionLoadingId === req.memberId}
                      onClick={() => handleDecline(req.memberId)}
                      sx={{ minHeight: 36 }}
                    >
                      Decline
                    </Button>
                  </Stack>
                </Stack>
              </Paper>
            ))}
          </Stack>
        )}
      </Box>

      {clubId && (
        <ScheduleDiveDialog
          open={scheduleDialogOpen}
          onClose={() => setScheduleDialogOpen(false)}
          onSaved={(dive) => {
            setOpenDives(prev => [...prev, dive].sort((a, b) => a.scheduledDate.localeCompare(b.scheduledDate)));
          }}
          initialOpenTo={{ clubId }}
        />
      )}

      {club && (
        <ClubSettingsDialog
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          club={club}
          onSave={handleSaveSettings}
        />
      )}
    </Box>
  );
};

export { DiveClubsList, DiveClubDetail };
